import * as React from 'react';
import { path } from 'ramda';
import { FormControl, InputLabel, MenuItem, Select, InternalStandardProps } from '@mui/material';
import { SelectProps } from '@mui/material/Select';
import classnames from 'classnames';
import { useStyles } from './styles';
import { useEditContextForInput } from '../useEditContextForInput';

interface SelectOption {
  value: string | number;
  label: string | number;
}

export type SelectInputProps = {
  field: string;
  label?: string;
  item?: Object;
  onChange?: (field: string, value: Object) => void;
  options?: SelectOption[];
  placeholder?: string;
  selectProps?: SelectProps;
} & InternalStandardProps<React.HTMLAttributes<HTMLDivElement>>;

const SelectInput: React.FC<SelectInputProps> = ({
  className,
  field,
  label,
  options,
  placeholder,
  selectProps,
  style,
  item,
  onChange,
}: SelectInputProps) => {
  const classes = useStyles();

  // If we provide an onChange function it prevails from using context
  let value: string | undefined;
  let props: SelectProps;
  if (onChange) {
    value = path(field.split('.'), item) || '';
    props = {
      onChange: (event) => onChange(field, event.target.value as Object),
    };
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { fieldValue, onChange: onInputChange } = useEditContextForInput<string>(field);
    value = fieldValue || '';
    props = {
      onChange: (event) => onInputChange(event.target.value as string),
    };
  }

  return (
    <FormControl className={classnames(classes.root, className)} variant="standard" style={style}>
      <InputLabel htmlFor={field}>{label || field}</InputLabel>
      <Select
        value={value}
        inputProps={{
          name: field,
          id: field,
        }}
        variant="standard"
        {...selectProps}
        {...props}
      >
        {placeholder && (
          <MenuItem value="">
            <em>{placeholder}</em>
          </MenuItem>
        )}
        {Array.isArray(options) &&
          options.map(({ label: optionLabel, value: optionValue }) => (
            <MenuItem key={optionValue} value={optionValue}>
              {optionLabel}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};

// eslint-disable-next-line import/no-default-export
export default SelectInput;
